<template>
    <div :class="componentClass">
        <C1Label
            v-if="label"
            :label-for="labelFor"
        >
            {{ label }}
        </C1Label>

        <div class="afu-relative afu-text-left">
            <div
                :class="buttonClassList"
                @click="toggleDropdown()"
            >
                {{ (localValue && localValue[labelKey]) ?  localValue[labelKey] : placeholder }}

                <span class="af-select-icon afu-text-secondary">
                    <!-- <svg
                        class="afu-h-4 afu-w-4 afu-ml-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg> -->
                    <FontAwesomeIcon
                        v-if="isOpen"
                        class="afu-ml-3"
                        :icon="['fas', 'caret-up']"
                    />
                    <FontAwesomeIcon
                        v-else
                        class="afu-ml-3"
                        :icon="['fas', 'caret-down']"
                    />
                </span>
            </div>

            <Transition
                enter-active-class="afu-transition afu-ease-out afu-duration-100"
                enter-class="afu-transform afu-opacity-0 afu-scale-95"
                enter-to-class="afu-transform afu-opacity-100 afu-scale-100"
                leave-active-class="afu-transition afu-ease-in afu-duration-75"
                leave-class="afu-transform afu-opacity-100 afu-scale-100"
                leave-to-class="afu-transform afu-opacity-0 afu-scale-95"
            >
                <div
                    v-show="isOpen"
                    class="afu-absolute afu-right-0 afu-mt-2 afu-w-full afu-rounded afu-shadow-lg afu-z-50"
                    style="max-height: 200px;"
                    :class="{ 'afu-overflow-y-scroll': scrollable }"
                >   
                    <ul class="af-dropdown afu-rounded afu-bg-white afu-shadow-xs afu-list-none afu-py-1 afu-px-0 afu-m-0">
                        <!-- <li
                            class="af-dropdown-first afu-block afu-px-4 afu-py-2 af-text--xl afu-text-gray-600 hover:afu-bg-gray-100 hover:afu-text-gray-600 focus:afu-outline-none focus:afu-bg-gray-100 afu-cursor-pointer afu-truncate"
                            @click="reset()"
                        >
                            {{ placeholder }}
                        </li> -->

                        <li
                            v-for="(opt, index) in options"
                            :key="`option_${opt[valueKey]}_${index}`"
                            class="afu-block afu-px-4 afu-py-2 afu-text-xl afu-text-black hover:afu-bg-neutral-light hover:afu-text-black focus:afu-outline-none focus:afu-bg-neutral-light afu-cursor-pointer"
                            @click="select(opt)"
                        >
                            {{ opt[labelKey] }}
                        </li>
                    </ul>
                </div>
            </Transition>
        </div>
        <C1HelpText
            v-if="invalidFeedback"
            variant="danger"
        >
            {{ invalidFeedback }}
        </C1HelpText>
    </div>
</template>

<script>
    import C1HelpText from './C1HelpText';
    import C1Label from './C1Label';

    export default {
        name: 'C1Select',

        components: {
            C1Label,
            C1HelpText,
        },

        props: {
            value: {
                type: Object,
                default: () => ({}),
            },

            placeholder: {
                type: String,
                default: 'Select',
            },

            label: {
                type: String,
                default: '',
            },

            labelFor: {
                type: String,
                default: '',
            },

            disabled: {
                type: Boolean,
                default: false,
            },

            scrollable: {
                type: Boolean,
                default: true,
            },

            size: {
                type: String,
                default: null,
                validator: value => ['sm', 'lg'].includes(value),
            },

            options: {
                type: Array,
                default: () => [],
            },

            labelKey: {
                type: String,
                default: 'label',
            },

            valueKey: {
                type: String,
                default: 'id',
            },

            isInvalid: {
                type: Boolean,
                default: false,
            },

            invalidFeedback: {
                type: [String, Boolean],
                default: false,
            },
        },

        data() {
            return {
                isOpen: false,
                id: this._uid,
                localValue: {},
            };
        },

        computed: {
            componentClass() {
                let output = ['af-select', (`af-profile-${this.labelFor}`)];
                if (this.size) {
                    output.push(`af-select-${this.size}`);
                }

                if (this.localValue && this.localValue[this.labelKey]) {
                    output.push('af-select-active');
                }

                if (this.isInvalid) {
                    output.push('af-select-invalid');
                }

                output.push('afu-inline-block afu-w-full');

                return output.join(' ');
            },

            buttonClassList() {
                return [
                    'af-select-btn afu-box-border afu-flex afu-items-center afu-justify-between afu-w-full afu-border-neutral afu-border-inputWidth afu-border-solid focus:afu-outline-none afu-transition afu-ease-in-out afu-duration-150 afu-z-50 afu-rounded-secondary afu-cursor-default',
                    this.getTextColorClass,
                    this.getTextSizeClass,
                    this.getBorderClass,
                    this.getBgColorClass,
                    this.getPaddingClass,
                ];
            },

            getTextColorClass() {
                return this.localValue && this.localValue[this.labelKey]
                    ? 'afu-text-black'
                    : 'afu-text-custom-one';
            },

            getTextSizeClass() {
                const sizes = {
                    default: 'afu-text-xl afu-font-normal',
                    sm: 'afu-text-sm afu-leading-4',
                    lg: 'afu-text-xl afu-font-normal',
                };

                return sizes[this.size] || sizes.default;
            },

            getBorderClass() {
                if (this.isInvalid) {
                    return 'afu-border-danger-400 focus:afu-border-danger-500';
                }

                if (this.disabled) {
                    return 'afu-border-neutral-light focus:afu-border-neutral-light';
                }

                return 'afu-border-neutral focus:afu-border-neutral-dark';
            },

            getBgColorClass() {
                if (this.disabled) {
                    return 'afu-bg-gray-100';
                }

                return 'afu-bg-white';
            },

            getPaddingClass() {
                const sizes = {
                    default: 'afu-p-btn',
                    sm: 'afu-py-2 afu-px-3',
                    lg: 'afu-p-btn-lg',
                };

                return sizes[this.size] || sizes.default;
            },
        },

        watch: {
            value(vl) {
                this.localValue = vl;
            },

            isOpen(value) {
                if (value) {
                    this.$root.$emit('afu::dropdown::shown', this);
                }
            },
        },

        created() {
            this.localValue = this.value;
            this.$root.$on('afu::dropdown::shown', this.rootCloseListener);
        },

        mounted() {
            if (typeof document !== 'undefined') {
                document.addEventListener('click', this.clickOutListener);
            }
        },

        beforeDestroy() {
            if (typeof document !== 'undefined') {
                document.removeEventListener('click', this.clickOutListener);
            }
        },

        methods: {
            rootCloseListener(vm) {
                if (vm !== this) {
                    this.close();
                }
            },

            clickOutListener(e) {
                if (!this.$el.contains(e.target)) {
                    this.close();
                }
            },

            toggleDropdown() {
                if (this.disabled) {
                    return;
                }

                this.isOpen ? this.close() : this.open();
            },

            open() {
                this.isOpen = true;
            },

            close() {
                this.isOpen = false;
            },

            select(opt) {
                this.close();
                this.localValue = opt;
                this.emit();
            },

            reset() {
                this.close();
                this.localValue = {};
                this.emit();
            },

            emit() {
                this.$emit('input', this.localValue);
            },
        },
    };
</script>
